import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, Moon, CalendarDays, UtensilsCrossed, SlidersHorizontal, X, Scale } from "lucide-react";
import { api } from "@/lib/api";
import { usePageMeta } from "@/lib/pageMeta";
import { siteUrl } from "@/lib/site";
import { track } from "@/lib/track";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SectionHeading } from "@/components/Reveal";
import { useOfferCompare } from "@/context/CompareContext";
import { useFeatureFlags } from "@/lib/featureFlags";
import TripPlansSection from "@/components/trip-plans/TripPlansSection";
import {
  OFFER_TYPES, cardPrice, nearestDateInfo, OFFER_PRICE_DISCLAIMER_SHORT,
  MEAL_FILTER_LABELS, TRANSFER_FILTER_LABELS, BUDGET_RANGES, activeOpts, offerNightsLabel,
} from "@/lib/offerShared";

export const OfferCard = ({ o }) => {
  const dateInfo = nearestDateInfo(o);
  const { has, toggle, items } = useOfferCompare();
  const flags = useFeatureFlags();
  const comparing = has(o.id);
  const nightsLabel = offerNightsLabel(o);
  return (
    <div
      data-testid={`offer-card-${o.slug}`}
      className="group relative h-full rounded-3xl border border-slate-200 bg-white overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-[#C80000]/30 hover:shadow-lg"
    >
      <Link to={`/offers/${o.slug}`} className="block h-full">
        {o.image?.file && (
          <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
            <img src={o.image.file} srcSet={o.image.srcset || undefined} sizes="(max-width: 640px) 100vw, 33vw" alt={o.image.caption || o.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
            {o.type && (
              <span className="absolute top-3 start-3 rounded-full bg-white/90 px-3 py-1 text-[11px] font-bold text-[#C80000] border border-[#C80000]/20">
                {(OFFER_TYPES.find((t) => t.id === o.type) || {}).label}
              </span>
            )}
          </div>
        )}
        <div className="p-5">
          <h3 className="text-lg font-bold text-[#1A1A1E] leading-snug">{o.title}</h3>
          {(o.countries?.length || o.cities?.length) > 0 && (
            <p className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-600">
              <MapPin className="h-3.5 w-3.5 text-[#C80000]" />
              {[...(o.countries || []), ...(o.cities || [])].join(" — ")}
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
            {flags.offersV2 && nightsLabel ? <span className="inline-flex items-center gap-1"><Moon className="h-3.5 w-3.5" />{nightsLabel}</span> : null}
            {flags.offersV2 && o.mealPlan ? <span className="inline-flex items-center gap-1"><UtensilsCrossed className="h-3.5 w-3.5" />{o.mealPlan}</span> : null}
            {dateInfo && <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{dateInfo}</span>}
          </div>
          <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-4">
            <span className="text-base font-bold text-[#C80000]" data-testid={`offer-card-price-${o.slug}`}>{cardPrice(o)}</span>
            <span className="text-xs font-bold text-slate-600 group-hover:text-[#C80000] transition-colors">التفاصيل ←</span>
          </div>
          <p className="mt-2 text-[10px] text-slate-400" data-testid={`offer-card-note-${o.slug}`}>{(flags.offersV2 || o.priceNote) ? OFFER_PRICE_DISCLAIMER_SHORT : ""}</p>
        </div>
      </Link>
      {flags.offersV2 && (
      <button
        type="button"
        onClick={() => toggle(o)}
        disabled={!comparing && items.length >= 3}
        aria-pressed={comparing}
        aria-label={`قارن ${o.title}`}
        data-testid={`offer-compare-${o.slug}`}
        className={`absolute top-3 end-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold border backdrop-blur transition-colors disabled:opacity-40 ${
          comparing ? "bg-[#1A1A1E] border-[#1A1A1E] text-white" : "bg-white/90 border-slate-200 text-slate-600 hover:border-[#C80000]/50 hover:text-[#C80000]"
        }`}
      >
        <Scale className="h-3 w-3" />
        {comparing ? "في المقارنة" : "قارن"}
      </button>
      )}
    </div>
  );
};

const Chip = ({ active, onClick, testid, children }) => (
  <button type="button" onClick={onClick} data-testid={testid} aria-pressed={active}
    className={`rounded-full px-4 py-2 text-xs font-bold border transition-colors ${active ? "bg-[#C80000] border-[#C80000] text-white" : "border-slate-300 bg-white text-slate-600 hover:border-[#C80000]/50 hover:text-[#C80000]"}`}>
    {children}
  </button>
);

const OffersPage = () => {
  const flags = useFeatureFlags();
  const [offers, setOffers] = useState(null);
  const [dests, setDests] = useState({ countries: [], cities: [] });
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [dest, setDest] = useState("all");
  const [budget, setBudget] = useState("all");
  const [nights, setNights] = useState("all");
  const [meal, setMeal] = useState("all");
  const [pool, setPool] = useState(false);
  const [slide, setSlide] = useState(false);
  const [transfer, setTransfer] = useState("all");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [tab, setTab] = useState("plans"); // «رحلات حسب طلبك» هو التبويب الافتراضي
  const [plans, setPlans] = useState(null);
  const [planQ, setPlanQ] = useState("");
  const [planDest, setPlanDest] = useState("all");

  usePageMeta("عروض السفر | دليل المسافر", siteUrl("/offers"), "عروض سفر موسمية وبكجات وكروزات من دليل المسافر للحجوزات — البحرين.");

  useEffect(() => {
    api.get("/offers").then(({ data }) => setOffers(data)).catch(() => setOffers([]));
    api.get("/offers/destinations").then(({ data }) => setDests(data)).catch(() => {});
    api.get("/trip-plans").then(({ data }) => setPlans(data)).catch(() => setPlans([]));
  }, []);

  // خيارات الفلاتر تُبنى مما هو موثق فعلًا في العروض المعروضة
  const available = useMemo(() => {
    const meals = new Set(); const transfers = new Set(); const nightsSet = new Set();
    let anyPool = false, anySlide = false;
    (offers || []).forEach((o) => {
      if (o.filters?.mealPlan) meals.add(o.filters.mealPlan);
      if (o.filters?.transfer) transfers.add(o.filters.transfer);
      if (o.filters?.privatePool) anyPool = true;
      if (o.filters?.slide) anySlide = true;
      activeOpts(o).forEach((x) => x.nights && nightsSet.add(Number(x.nights)));
    });
    return { meals: [...meals], transfers: [...transfers], nights: [...nightsSet].sort((a, b) => a - b), anyPool, anySlide };
  }, [offers]);

  // أرخص خيار سارٍ = أساس فلترة الميزانية (الأسعار المنتهية مستبعدة من الخادم)
  const fromBHD = (o) => {
    const fp = o.fromPrice || {};
    const opts = activeOpts(o).filter((x) => !x.askPrice && x.priceBHD && x.optionKind !== "extraNight");
    const chosen = fp.enabled ? opts.find((x) => x.id === fp.optionId) : null;
    const eff = chosen || [...opts].sort((a, b) => a.priceBHD - b.priceBHD)[0];
    return eff ? Number(eff.priceBHD) : null;
  };

  const filtered = useMemo(() => {
    if (!offers) return [];
    const needle = q.trim().toLowerCase();
    const range = BUDGET_RANGES.find((r) => r.id === budget);
    return offers.filter((o) => {
      if (type !== "all" && o.type !== type) return false;
      if (dest !== "all" && !(o.countries || []).includes(dest) && !(o.cities || []).includes(dest)) return false;
      if (needle && !(o.title || "").toLowerCase().includes(needle) && ![...(o.countries || []), ...(o.cities || [])].join(" ").toLowerCase().includes(needle) && !(o.searchAliases || []).join(" ").toLowerCase().includes(needle)) return false;
      if (range) {
        const p = fromBHD(o);
        if (p === null || p < range.min || p > range.max) return false;
      }
      if (nights !== "all" && !activeOpts(o).some((x) => Number(x.nights) === Number(nights))) return false;
      if (meal !== "all" && o.filters?.mealPlan !== meal) return false;
      if (pool && !o.filters?.privatePool) return false;
      if (slide && !o.filters?.slide) return false;
      if (transfer !== "all" && o.filters?.transfer !== transfer) return false;
      return true;
    });
  }, [offers, q, type, dest, budget, nights, meal, pool, slide, transfer]);

  const activeFilterCount = [budget !== "all", nights !== "all", meal !== "all", pool, slide, transfer !== "all"].filter(Boolean).length;
  const clearFilters = () => { setBudget("all"); setNights("all"); setMeal("all"); setPool(false); setSlide(false); setTransfer("all"); };
  const trackFilter = (name) => track("filter_use", name);
  // لا يظهر تبويب «عروض بأسعار معلنة» إلا إذا وُجدت عروض منشورة فعلًا — الصفحة للمخططات وحدها عند غيابها
  const hasOffers = (offers || []).length > 0;

  const destinations = useMemo(() => [...dests.countries, ...dests.cities.filter((c) => !dests.countries.includes(c))], [dests]);

  // وجهات المخططات تُبنى من سجلاتها المنشورة الفعلية
  const planDests = useMemo(() => {
    const set = [];
    (plans || []).forEach((p) => [...(p.countries || []), ...(p.cities || [])].forEach((x) => { if (!set.includes(x)) set.push(x); }));
    return set;
  }, [plans]);

  const filteredPlans = useMemo(() => {
    const needle = planQ.trim().toLowerCase();
    return (plans || []).filter((p) => {
      if (planDest !== "all" && ![...(p.countries || []), ...(p.cities || [])].includes(planDest)) return false;
      if (needle && !(p.title || "").toLowerCase().includes(needle) && ![...(p.countries || []), ...(p.cities || [])].join(" ").toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [plans, planQ, planDest]);

  const clearPlanFilters = () => { setPlanQ(""); setPlanDest("all"); };

  return (
    <div className="min-h-screen bg-white text-[#1A1A1E]">
      <Navbar />
      <main className="mx-auto max-w-7xl px-5 sm:px-8 pt-28 pb-20">
        <SectionHeading caption="عروض دليل المسافر" title="عروض السفر" as="h1" />
        <p className="mt-4 max-w-2xl text-sm sm:text-base text-slate-600 leading-relaxed">
          {hasOffers
            ? "«رحلات حسب طلبك» نصمّمها لك من الصفر بلا سعر ثابت، و«عروض بأسعار معلنة» جاهزة بأسعارها — الأسعار والتوفر تُؤكد عند التواصل."
            : "«رحلات حسب طلبك» نصمّمها لك من الصفر بلا سعر ثابت — تواصل معنا ونرتّب لك الرحلة كاملة، والسعر والتوفر يُؤكدان عند التواصل."}
        </p>

        {hasOffers && (
        <div className="mt-8 w-full max-w-xl grid grid-cols-2 gap-1.5 rounded-2xl border border-slate-200 bg-[#F8F9FA] p-1.5" role="tablist" data-testid="offers-tabs">
          {[
            { id: "plans", label: "رحلات حسب طلبك", count: plans?.length, testid: "offers-tab-plans" },
            { id: "offers", label: "عروض بأسعار معلنة", count: offers?.length, testid: "offers-tab-priced" },
          ].map((t) => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)} data-testid={t.testid}
              className={`flex items-center justify-center gap-2 rounded-xl px-3 py-3 text-sm font-bold transition-colors ${tab === t.id ? "bg-[#C80000] text-white shadow-md shadow-[#C80000]/25" : "text-slate-600 hover:text-[#C80000]"}`}>
              {t.label}
              <span data-testid={`${t.testid}-count`} className={`rounded-full px-2 py-0.5 text-[11px] font-black ${tab === t.id ? "bg-white/20 text-white" : "bg-slate-200 text-slate-600"}`}>
                {t.count ?? "…"}
              </span>
            </button>
          ))}
        </div>
        )}

        {tab === "plans" && (
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search className="absolute start-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input value={planQ} onChange={(e) => setPlanQ(e.target.value)} placeholder="ابحث باسم المخطط أو الوجهة…" data-testid="trip-plans-search"
                className="w-full sm:w-72 rounded-full bg-[#F8F9FA] border border-slate-200 ps-11 pe-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#C80000]/50" />
            </div>
            {planDests.length > 0 && (
              <select value={planDest} onChange={(e) => setPlanDest(e.target.value)} data-testid="trip-plans-destination-filter"
                className="rounded-full bg-[#F8F9FA] border border-slate-200 px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#C80000]/50">
                <option value="all">كل الوجهات</option>
                {planDests.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            )}
          </div>
        )}

        {tab === "offers" && (
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="absolute start-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث باسم العرض أو الوجهة…" data-testid="offers-search"
              className="w-full sm:w-72 rounded-full bg-[#F8F9FA] border border-slate-200 ps-11 pe-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#C80000]/50" />
          </div>
          {destinations.length > 0 && (
            <select value={dest} onChange={(e) => { setDest(e.target.value); if (e.target.value !== "all") trackFilter("destination"); }} data-testid="offers-destination-filter"
              className="rounded-full bg-[#F8F9FA] border border-slate-200 px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#C80000]/50">
              <option value="all">كل الوجهات</option>
              {destinations.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          )}
          <div className="flex flex-wrap gap-2">
            <Chip active={type === "all"} onClick={() => setType("all")} testid="offers-type-all">الكل</Chip>
            {OFFER_TYPES.filter((t) => (offers || []).some((o) => o.type === t.id)).map((t) => (
              <Chip key={t.id} active={type === t.id} onClick={() => setType(t.id)} testid={`offers-type-${t.id}`}>{t.label}</Chip>
            ))}
          </div>
          {flags.offersV2 && (
          <button type="button" onClick={() => setFiltersOpen((v) => !v)} data-testid="offers-filters-toggle" aria-expanded={filtersOpen}
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-xs font-bold transition-colors ${filtersOpen || activeFilterCount ? "border-[#C80000] text-[#C80000]" : "border-slate-300 text-slate-600 hover:border-[#C80000]/50"}`}>
            <SlidersHorizontal className="h-3.5 w-3.5" />
            الفلاتر{activeFilterCount ? ` (${activeFilterCount})` : ""}
          </button>
          )}
          {flags.offersV2 && activeFilterCount > 0 && (
            <button type="button" onClick={clearFilters} data-testid="offers-clear-filters"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-slate-500 hover:text-red-600 hover:border-red-300 transition-colors">
              <X className="h-3.5 w-3.5" /> مسح الفلاتر
            </button>
          )}
        </div>
        )}

        {tab === "offers" && flags.offersV2 && filtersOpen && (
          <div className="mt-4 space-y-4 rounded-3xl border border-slate-200 bg-[#F8F9FA] p-5" data-testid="offers-filters-panel">
            <div>
              <p className="mb-2 text-xs font-bold text-slate-600">الميزانية <span className="font-normal text-slate-400">— حسب «ابتداءً من» الساري، إجمالي لشخصين في عروض المالديف</span></p>
              <div className="flex flex-wrap gap-2">
                <Chip active={budget === "all"} onClick={() => setBudget("all")} testid="offers-budget-all">أي ميزانية</Chip>
                {BUDGET_RANGES.map((r) => (
                  <Chip key={r.id} active={budget === r.id} onClick={() => { setBudget(r.id); trackFilter("budget"); }} testid={`offers-budget-${r.id}`}>{r.label}</Chip>
                ))}
              </div>
            </div>
            {available.nights.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-bold text-slate-600">عدد الليالي</p>
                <div className="flex flex-wrap gap-2">
                  <Chip active={nights === "all"} onClick={() => setNights("all")} testid="offers-nights-all">أي عدد</Chip>
                  {available.nights.map((n) => (
                    <Chip key={n} active={String(nights) === String(n)} onClick={() => { setNights(String(n)); trackFilter("nights"); }} testid={`offers-nights-${n}`}>{n} ليالٍ</Chip>
                  ))}
                </div>
              </div>
            )}
            {available.meals.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-bold text-slate-600">نظام الوجبات</p>
                <div className="flex flex-wrap gap-2">
                  <Chip active={meal === "all"} onClick={() => setMeal("all")} testid="offers-meal-all">الكل</Chip>
                  {available.meals.map((m) => (
                    <Chip key={m} active={meal === m} onClick={() => { setMeal(m); trackFilter("meal"); }} testid={`offers-meal-${m}`}>{MEAL_FILTER_LABELS[m] || m}</Chip>
                  ))}
                </div>
              </div>
            )}
            {available.transfers.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-bold text-slate-600">نوع الانتقال</p>
                <div className="flex flex-wrap gap-2">
                  <Chip active={transfer === "all"} onClick={() => setTransfer("all")} testid="offers-transfer-all">الكل</Chip>
                  {available.transfers.map((t) => (
                    <Chip key={t} active={transfer === t} onClick={() => { setTransfer(t); trackFilter("transfer"); }} testid={`offers-transfer-${t}`}>{TRANSFER_FILTER_LABELS[t] || t}</Chip>
                  ))}
                </div>
              </div>
            )}
            {(available.anyPool || available.anySlide) && (
              <div>
                <p className="mb-2 text-xs font-bold text-slate-600">مرافق الفيلا <span className="font-normal text-slate-400">— وفق اسم الفئة الموثق في العرض</span></p>
                <div className="flex flex-wrap gap-2">
                  {available.anyPool && <Chip active={pool} onClick={() => { setPool(!pool); if (!pool) trackFilter("pool"); }} testid="offers-pool">مسبح خاص</Chip>}
                  {available.anySlide && <Chip active={slide} onClick={() => { setSlide(!slide); if (!slide) trackFilter("slide"); }} testid="offers-slide">زحليقة مائية</Chip>}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "offers" && (offers === null ? (
          <p className="mt-16 text-center text-slate-500" data-testid="offers-loading">جارٍ التحميل…</p>
        ) : filtered.length === 0 ? (
          <div className="mt-12 rounded-3xl border border-slate-200 bg-[#F8F9FA] p-10 text-center" data-testid="offers-empty">
            <p className="font-bold text-lg text-[#1A1A1E]">لا توجد عروض مطابقة للفلاتر المختارة</p>
            <p className="mt-2 text-sm text-slate-500">جرّب تعديل البحث أو مسح الفلاتر، أو تواصل معنا لتصميم عرض خاص بك.</p>
            {activeFilterCount > 0 && (
              <button type="button" onClick={clearFilters} data-testid="offers-empty-clear"
                className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#C80000] px-6 py-2.5 text-xs font-bold text-white hover:bg-[#A60000] transition-colors">
                <X className="h-3.5 w-3.5" /> مسح الفلاتر
              </button>
            )}
          </div>
        ) : (
          <>
            {flags.offersV2 && <p className="mt-6 text-xs text-slate-500" data-testid="offers-count">{filtered.length} من {offers.length} عرضًا</p>}
            <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="offers-grid">
              {filtered.map((o) => <OfferCard key={o.id} o={o} />)}
            </div>
          </>
        ))}

        {tab === "plans" && (plans === null ? (
          <p className="mt-16 text-center text-slate-500" data-testid="trip-plans-loading">جارٍ التحميل…</p>
        ) : filteredPlans.length === 0 ? (
          <div className="mt-12 rounded-3xl border border-slate-200 bg-[#F8F9FA] p-10 text-center" data-testid="trip-plans-empty">
            <p className="font-bold text-lg text-[#1A1A1E]">لا توجد رحلات مطابقة للبحث أو الوجهة المختارة</p>
            <p className="mt-2 text-sm text-slate-500">جرّب تعديل البحث أو اختر وجهة أخرى، أو تواصل معنا لتصميم رحلة خاصة بك.</p>
            <button type="button" onClick={clearPlanFilters} data-testid="trip-plans-empty-clear"
              className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#C80000] px-6 py-2.5 text-xs font-bold text-white hover:bg-[#A60000] transition-colors">
              <X className="h-3.5 w-3.5" /> مسح البحث
            </button>
          </div>
        ) : (
          <TripPlansSection plans={filteredPlans} bare />
        ))}
      </main>
      <Footer />
    </div>
  );
};

export default OffersPage;
